import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Mail, ArrowLeft, RefreshCw, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/apple-design-system.css';
import ThemeToggle from '@/components/apple-ui/ThemeToggle';
import { useTheme } from '@/contexts/ThemeContext';
import SignupLeftPanel from '@/components/Auth/SignupLeftPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SignupPageApple = ({ onLogin }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [isMobileOrTablet, setIsMobileOrTablet] = React.useState(window.innerWidth <= 1024);

  // Capture affiliate ref code from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      sessionStorage.setItem('affiliate_ref_code', ref);
    }
  }, []);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobileOrTablet(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [step, setStep] = useState('email');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    country: '',
    state: '',
    city: '',
    address: '',
    postal_code: '',
    password: '',
    confirmPassword: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({ password: false, confirm: false });
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Resend OTP cooldown
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);
  
  // OTP input refs
  const otpRefs = useRef([]);

  // Cleanup cooldown timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  // Start cooldown timer
  const startCooldown = () => {
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
    setError('');
  };

  // Handle email/name/password submission - send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    
    const cleaned = Object.fromEntries(Object.entries(formData).map(([key, value]) => [key, value.trim()]));
    const nextFieldErrors = {};
    const requiredFields = [
      ['name', 'Full Name'], ['email', 'Email'], ['phone', 'Phone Number'], ['country', 'Country'],
      ['state', 'State / Province'], ['city', 'City'], ['address', 'Address'], ['postal_code', 'Postal / ZIP Code'],
      ['password', 'Password'], ['confirmPassword', 'Confirm Password']
    ];
    requiredFields.forEach(([key, label]) => {
      if (!cleaned[key]) nextFieldErrors[key] = `${label} is required`;
    });
    if (cleaned.email && !/^\S+@\S+\.\S+$/.test(cleaned.email)) nextFieldErrors.email = 'Please enter a valid email address';
    const phoneDigits = cleaned.phone.replace(/\D/g, '');
    if (cleaned.phone && (!/^\+?[0-9() .-]+$/.test(cleaned.phone) || phoneDigits.length < 7 || phoneDigits.length > 15)) {
      nextFieldErrors.phone = 'Please enter a valid phone number';
    }
    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors(nextFieldErrors);
      setError('Please complete all required fields.');
      return;
    }
    setFormData(cleaned);
    setFieldErrors({});

    if (cleaned.password.length < 8) {
      setFieldErrors({ password: 'Password must be at least 8 characters' });
      setError('Password must be at least 8 characters');
      return;
    }

    if (cleaned.password !== cleaned.confirmPassword) {
      setFieldErrors({ confirmPassword: 'Passwords do not match' });
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned.email, purpose: 'signup' })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('OTP sent to your email');
        setStep('otp');
        startCooldown();
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        setError(data.detail || 'Failed to send OTP');
        toast.error(data.detail || 'Failed to send OTP');
      }
    } catch (err) {
      console.error('Send OTP error:', err);
      setError('Connection error. Please try again.');
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);
    setError('');
    
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
    
    if (value && index === 5 && newOtp.every(d => d)) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtpCode(digits);
      otpRefs.current[5]?.focus();
      setTimeout(() => handleVerifyOTP(pastedData), 100);
    }
  };

  // Handle backspace in OTP input
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Verify OTP and create account
  const handleVerifyOTP = async (code = null) => {
    const finalCode = code || otpCode.join('');
    
    if (finalCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const refCode = sessionStorage.getItem('affiliate_ref_code') || null;
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim(),
          otp_code: finalCode,
          purpose: 'signup',
          name: formData.name.trim(),
          password: formData.password.trim(),
          confirm_password: formData.confirmPassword.trim(),
          phone: formData.phone.trim(),
          country: formData.country.trim(),
          state: formData.state.trim(),
          city: formData.city.trim(),
          address: formData.address.trim(),
          postal_code: formData.postal_code.trim(),
          ref_code: refCode,
        })
      });

      const data = await response.json();

      if (response.ok) {
        sessionStorage.removeItem('affiliate_ref_code');
        toast.success('Account created successfully!');
        localStorage.setItem('token', data.token);
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        if (data.plan_info) {
          localStorage.setItem('plan_info', JSON.stringify(data.plan_info));
        }
        if (onLogin) {
          await onLogin(data.token, data.user, data.plan_info, true);
        }
        // Navigation handled by SignupWrapper based on plan param
      } else {
        setError(data.detail || 'Invalid OTP');
        toast.error(data.detail || 'Verification failed');
        setOtpCode(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError('Connection error. Please try again.');
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email.trim(), purpose: 'signup' })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('New OTP sent');
        startCooldown();
        setOtpCode(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      } else {
        setError(data.detail || 'Failed to resend OTP');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Go back to email step
  const handleBackToEmail = () => {
    setStep('email');
    setOtpCode(['', '', '', '', '', '']);
    setError('');
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setResendCooldown(0);
  };

  return (
    <div 
      className="signup-page-apple-split" 
      data-testid="apple-signup-page"
      style={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%'
      }}
    >
      {/* Left Panel — desktop only */}
      {!isMobileOrTablet && <SignupLeftPanel />}

      {/* Right Panel - Signup Form (55% on desktop, 100% on mobile) */}
      <div 
        className="signup-right-panel"
        style={{
          flex: isMobileOrTablet ? '1 1 100%' : '1 1 55%',
          background: isDarkMode ? '#000000' : '#ffffff',
          padding: isMobileOrTablet ? '40px 24px' : '40px 60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          overflowY: 'auto'
        }}
      >

        {/* Theme Toggle */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 10 }}>
          <ThemeToggle />
        </div>

        <div style={{ width: '100%', maxWidth: '380px' }}>
          {/* Logo */}
          <div style={{
            textAlign: 'center',
            marginBottom: '32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div style={{
              width: '44px',
              height: '44px',
              background: '#000',
              borderRadius: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginBottom: '12px',
              boxShadow: isDarkMode
                ? '0 0 0 2px #ffffff'
                : '0 0 0 1px rgba(0,86,255,.30), 0 4px 20px rgba(0,86,255,.22)'
            }}>
              <img
                src="/logo-marketing.png"
                alt="t09n.com"
                style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: 0, display: 'block' }}
              />
            </div>
            <span style={{
              fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: 700,
              fontStyle: 'normal',
              fontSize: '18px',
              color: isDarkMode ? '#ffffff' : '#000000',
              letterSpacing: '-0.3px',
              lineHeight: 1.1
            }}>
              t09n<span style={{ fontWeight: 700 }}>.com</span>
            </span>
            <span style={{
              fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif',
              marginTop: '3px',
              fontSize: '11px',
              color: isDarkMode ? '#ffffff' : '#000000',
              fontWeight: 700,
              fontStyle: 'oblique',
              lineHeight: 1.2,
              letterSpacing: '0.1px'
            }}>
              The Operating System For Translators
            </span>
          </div>

          {/* Form Card */}
          <div style={{
            background: isDarkMode ? '#1c1c1e' : '#f5f5f7',
            borderRadius: '20px',
            padding: '32px',
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`
          }}>
            {step === 'email' && (
              <>
                <h3 style={{
                  fontSize: '22px',
                  fontWeight: '600',
                  color: isDarkMode ? '#ffffff' : '#1d1d1f',
                  marginBottom: '8px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                  Create your account
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#86868b',
                  marginBottom: '24px'
                }}>
                  Start with 500 free words every month
                </p>

                {/* Error Message */}
                {error && (
                  <div style={{
                    background: isDarkMode ? 'rgba(255,59,48,0.15)' : '#FEE2E2',
                    color: '#FF3B30',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }} data-testid="signup-error-message">
                    <AlertCircle size={18} />
                    {error}
                  </div>
                )}

                <form onSubmit={handleSendOTP} noValidate data-testid="signup-form">
                  {/* Name */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: isDarkMode ? 'rgba(255,255,255,0.8)' : '#1d1d1f',
                      marginBottom: '6px'
                    }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                      data-testid="signup-name-input"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        fontSize: '16px',
                        borderRadius: '12px',
                        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}`,
                        background: isDarkMode ? '#2c2c2e' : '#ffffff',
                        color: isDarkMode ? '#ffffff' : '#1d1d1f',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#0071e3'}
                      onBlur={(e) => e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}
                    />
                    {fieldErrors.name && <p style={{ color: '#FF3B30', fontSize: '12px', margin: '5px 0 0' }}>{fieldErrors.name}</p>}
                  </div>

                  {/* Email */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: isDarkMode ? 'rgba(255,255,255,0.8)' : '#1d1d1f',
                      marginBottom: '6px'
                    }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      data-testid="signup-email-input"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        fontSize: '16px',
                        borderRadius: '12px',
                        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}`,
                        background: isDarkMode ? '#2c2c2e' : '#ffffff',
                        color: isDarkMode ? '#ffffff' : '#1d1d1f',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#0071e3'}
                      onBlur={(e) => e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}
                    />
                    {fieldErrors.email && <p style={{ color: '#FF3B30', fontSize: '12px', margin: '5px 0 0' }}>{fieldErrors.email}</p>}
                  </div>

                  {[
                    ['phone', 'Phone Number *', 'Phone number', 'tel'],
                    ['country', 'Country *', 'Country', 'text'],
                    ['state', 'State / Province *', 'State or province', 'text'],
                    ['city', 'City *', 'City', 'text'],
                    ['postal_code', 'Postal / ZIP Code *', 'Postal or ZIP code', 'text']
                  ].map(([key, label, placeholder, type]) => (
                    <div key={key} style={{ marginBottom: '16px' }}>
                      <label htmlFor={`signup-${key}`} style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: isDarkMode ? 'rgba(255,255,255,0.8)' : '#1d1d1f', marginBottom: '6px' }}>{label}</label>
                      <input
                        id={`signup-${key}`} type={type} name={key} placeholder={placeholder}
                        value={formData[key]} onChange={handleChange} autoComplete={key === 'phone' ? 'tel' : 'off'}
                        data-testid={`signup-${key}-input`}
                        style={{ width: '100%', padding: '14px 16px', fontSize: '16px', borderRadius: '12px', border: `1px solid ${fieldErrors[key] ? '#FF3B30' : (isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6')}`, background: isDarkMode ? '#2c2c2e' : '#ffffff', color: isDarkMode ? '#ffffff' : '#1d1d1f', outline: 'none', boxSizing: 'border-box' }}
                      />
                      {fieldErrors[key] && <p style={{ color: '#FF3B30', fontSize: '12px', margin: '5px 0 0' }}>{fieldErrors[key]}</p>}
                    </div>
                  ))}

                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="signup-address" style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: isDarkMode ? 'rgba(255,255,255,0.8)' : '#1d1d1f', marginBottom: '6px' }}>Address *</label>
                    <textarea
                      id="signup-address" name="address" placeholder="Street address" value={formData.address}
                      onChange={handleChange} autoComplete="street-address" rows={3} data-testid="signup-address-input"
                      style={{ width: '100%', padding: '14px 16px', fontSize: '16px', borderRadius: '12px', border: `1px solid ${fieldErrors.address ? '#FF3B30' : (isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6')}`, background: isDarkMode ? '#2c2c2e' : '#ffffff', color: isDarkMode ? '#ffffff' : '#1d1d1f', outline: 'none', boxSizing: 'border-box', resize: 'vertical' }}
                    />
                    {fieldErrors.address && <p style={{ color: '#FF3B30', fontSize: '12px', margin: '5px 0 0' }}>{fieldErrors.address}</p>}
                  </div>

                  {/* Password */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: isDarkMode ? 'rgba(255,255,255,0.8)' : '#1d1d1f', marginBottom: '6px' }}>
                      Password *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPasswords.password ? 'text' : 'password'}
                        name="password"
                        placeholder="Min 8 chars, upper, lower, number, symbol"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        autoComplete="new-password"
                        data-testid="signup-password-input"
                        style={{
                          width: '100%', padding: '14px 42px 14px 16px', fontSize: '16px', borderRadius: '12px',
                          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}`,
                          background: isDarkMode ? '#2c2c2e' : '#ffffff', color: isDarkMode ? '#ffffff' : '#1d1d1f',
                          outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#0071e3'}
                        onBlur={(e) => e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}
                      />
                      <button type="button" onClick={() => setShowPasswords(p => ({ ...p, password: !p.password }))}
                        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#86868b', padding: 0 }}>
                        {showPasswords.password ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {fieldErrors.password && <p style={{ color: '#FF3B30', fontSize: '12px', margin: '5px 0 0' }}>{fieldErrors.password}</p>}
                  </div>

                  {/* Confirm Password */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: isDarkMode ? 'rgba(255,255,255,0.8)' : '#1d1d1f', marginBottom: '6px' }}>
                      Confirm Password *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        name="confirmPassword"
                        placeholder="Re-enter your password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        autoComplete="new-password"
                        data-testid="signup-confirm-password-input"
                        style={{
                          width: '100%', padding: '14px 42px 14px 16px', fontSize: '16px', borderRadius: '12px',
                          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}`,
                          background: isDarkMode ? '#2c2c2e' : '#ffffff', color: isDarkMode ? '#ffffff' : '#1d1d1f',
                          outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#0071e3'}
                        onBlur={(e) => e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#d1d1d6'}
                      />
                      <button type="button" onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#86868b', padding: 0 }}>
                        {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {fieldErrors.confirmPassword && <p style={{ color: '#FF3B30', fontSize: '12px', margin: '5px 0 0' }}>{fieldErrors.confirmPassword}</p>}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="signup-submit-btn"
                    style={{
                      width: '100%',
                      padding: '16px',
                      fontSize: '17px',
                      fontWeight: '600',
                      borderRadius: '12px',
                      border: 'none',
                      background: '#007AFF',
                      color: '#ffffff',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'opacity 0.2s, transform 0.1s',
                      fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
                    }}
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending OTP...
                      </>
                    ) : (
                      <>
                        <Mail size={18} />
                        Continue with Email
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            {/* Step 2: OTP Verification */}
            {step === 'otp' && (
              <>
                {/* Back button */}
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: isDarkMode ? '#6B9AFF' : '#0066CC',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginBottom: '16px',
                    padding: 0
                  }}
                  data-testid="back-to-email-btn"
                >
                  <ArrowLeft size={16} />
                  Back
                </button>

                <h3 style={{
                  fontSize: '22px',
                  fontWeight: '600',
                  color: isDarkMode ? '#ffffff' : '#1d1d1f',
                  marginBottom: '8px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                  Verify your email
                </h3>
                
                <p style={{
                  fontSize: '14px',
                  color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#86868b',
                  marginBottom: '24px'
                }}>
                  Enter the 6-digit code sent to<br />
                  <strong style={{ color: isDarkMode ? '#fff' : '#000' }}>{formData.email}</strong>
                </p>

                {error && (
                  <div style={{
                    background: isDarkMode ? 'rgba(255,59,48,0.15)' : '#FEE2E2',
                    color: '#FF3B30',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }} data-testid="otp-error-message">
                    <AlertCircle size={18} />
                    {error}
                  </div>
                )}

                {/* OTP Input boxes */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '20px'
                }}>
                  {otpCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => otpRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={index === 0 ? handleOtpPaste : undefined}
                      style={{
                        width: '46px',
                        height: '54px',
                        fontSize: '24px',
                        fontWeight: '600',
                        textAlign: 'center',
                        borderRadius: '12px',
                        border: `2px solid ${error ? '#FF3B30' : (isDarkMode ? 'rgba(255,255,255,0.2)' : '#d1d1d6')}`,
                        background: isDarkMode ? '#2c2c2e' : '#fff',
                        color: isDarkMode ? '#fff' : '#000',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#007AFF'}
                      onBlur={(e) => e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.2)' : '#d1d1d6'}
                      data-testid={`otp-input-${index}`}
                    />
                  ))}
                </div>

                {/* Verify button */}
                <button
                  type="button"
                  onClick={() => handleVerifyOTP()}
                  disabled={loading || otpCode.join('').length !== 6}
                  data-testid="verify-otp-btn"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '17px',
                    fontWeight: '600',
                    borderRadius: '12px',
                    border: 'none',
                    background: '#007AFF',
                    color: '#ffffff',
                    cursor: (loading || otpCode.join('').length !== 6) ? 'not-allowed' : 'pointer',
                    opacity: (loading || otpCode.join('').length !== 6) ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Verify & Create Account'
                  )}
                </button>

                {/* Resend OTP */}
                <div style={{
                  marginTop: '16px',
                  textAlign: 'center'
                }}>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || loading}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: resendCooldown > 0 ? (isDarkMode ? 'rgba(255,255,255,0.4)' : '#999') : (isDarkMode ? '#6B9AFF' : '#0066CC'),
                      fontSize: '14px',
                      cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    data-testid="resend-otp-btn"
                  >
                    <RefreshCw size={14} />
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Didn't receive code? Resend"}
                  </button>
                </div>
              </>
            )}

            {/* Footer Link */}
            <p style={{
              textAlign: 'center',
              marginTop: '20px',
              fontSize: '14px',
              color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#86868b'
            }}>
              Already have an account?{' '}
              <Link 
                to="/login" 
                data-testid="goto-login-link"
                style={{
                  color: '#007AFF',
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
              >
                Sign in
              </Link>
            </p>
          </div>

          {/* Company Footer */}
          <div style={{
            marginTop: '24px',
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: '12px',
              color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
              margin: '0 0 4px 0'
            }}>
              © 2026 t09n.com. All rights reserved.
            </p>
            <p style={{
              fontSize: '11px',
              color: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              margin: '0 0 4px 0',
              fontStyle: 'italic'
            }}>
              Operated by Localization Lounge · GSTIN: 24BSWPM3389R1ZG
            </p>
            <p style={{
              fontSize: '11px',
              color: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              margin: '0'
            }}>
              Support:{' '}
              <a 
                href="mailto:support@t09n.com" 
                style={{ 
                  color: isDarkMode ? '#6B9AFF' : '#0066CC',
                  textDecoration: 'none'
                }}
              >
                support@t09n.com
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Responsive styles */}
      <style>{`
        /* Mobile & Tablet: hide left panel, full width form */
        @media (max-width: 1024px) {
          .signup-left-panel {
            display: none !important;
          }
          .signup-right-panel {
            flex: 1 1 100% !important;
            padding: 40px 24px !important;
            min-height: 100vh !important;
          }
        }

        /* Mobile: tighter padding */
        @media (max-width: 480px) {
          .signup-right-panel {
            padding: 32px 16px !important;
          }
        }

        /* Desktop only: show both panels */
        @media (min-width: 1025px) {
          .signup-left-panel {
            display: flex !important;
            width: 45% !important;
          }
          .signup-right-panel {
            flex: 1 1 55% !important;
            padding: 40px 60px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SignupPageApple;
